import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DynamicForm from "../common/DynamicForm";
import { AlertDialog } from "../common/AlertDailog";
import FormFields from "./siteFields.json";
import { siteSchema } from "../../config/schemas/siteSchema";
import { useSites } from "../../hooks/useSites";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useAppSelector, AppState, useAppDispatch } from "../../store";
import {
  setIsEditSite,
  setOpenAddSiteModal,
  setSiteDetails,
} from "../../store/slices/studiesSlice";
import { useParams } from "react-router-dom";
import { codelistUtils } from "../../utils/codelistUtils";

const SiteDialog: React.FunctionComponent<any> = (props) => {
  const { getAllSitesFunc, paginationModel, filterVal ,codelistData} = props;
  const dispatch = useAppDispatch();
  const openAddSiteModal = useAppSelector(
    (state: AppState) => state.studiesSlice.openAddSiteModal
  );
  const params = useParams();
  const siteData = useAppSelector(
    (state: AppState) => state.studiesSlice.siteDetails
  );
  const isEditSite = useAppSelector(
    (state: AppState) => state.studiesSlice.isEditSite
  );
  const values = isEditSite && {
    ...siteData,
    country: {
      code_list_value: codelistData?.length>0 && codelistUtils(codelistData,siteData?.country_code,siteData?.country_value_code)?.selectedValue,
      code_list_value_code: siteData?.country_value_code,
    },
    site_status: {
      code_list_value: codelistData?.length>0 && codelistUtils(codelistData,siteData?.site_status_code,siteData?.site_status_value_code)?.selectedValue,
      code_list_value_code: siteData?.site_status_value_code,
    },
  };
  const {
    createSite,
    createSiteData,
    createSiteIsError,
    createSiteError,
    createSiteIsSuccess,
    updateSite,
    updateSiteData,
    updateSiteIsError,
    updateSiteError,
    updateSiteIsSuccess,
    getSiteDetails,
  } = useSites();
  const { showSuccess, handleError } = useSnackbar();
  type FormValues = z.infer<typeof siteSchema>;
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setFocus,
  } = useForm<FormValues>({
    values,
    resolver: zodResolver(siteSchema),
    defaultValues: {},
  });

  const onInvalid = (errors: any) => console.debug(errors);
  const submit = (data: any) => {
    isEditSite ? handleUpdateSite(data) : handleCreateSite(data);
  };
  const handleCreateSite = (siteData: any) => {
    createSite({
      data: {
        ...siteData,
        study_id: params?.study_id,
        country: siteData?.country?.code_list_value,
        country_code: FormFields?.CNTRY,
        country_value_code: siteData?.country?.code_list_value_code,
        site_status: siteData?.site_status?.code_list_value,
        site_status_code: FormFields?.SITE_STATUS,
        site_status_value_code: siteData?.site_status?.code_list_value_code,
      },
    });
  };
  const handleUpdateSite = (siteData: any) => {
    updateSite({
      data: {
        ...siteData,
        study_id: params?.study_id,
        country: siteData?.country?.code_list_value,
        country_code: FormFields?.CNTRY,
        country_value_code: siteData?.country?.code_list_value_code,
        site_status: siteData?.site_status?.code_list_value,
        site_status_code: FormFields?.SITE_STATUS,
        site_status_value_code: siteData?.site_status?.code_list_value_code,
      },
    });
  };
  const onCloseSiteDialog = () => {
    dispatch(setOpenAddSiteModal(false));
    dispatch(setIsEditSite(false));
  };
  useEffect(() => {
    const firstError = Object.keys(errors)?.reduce((field: any, a) => {
      return (errors as any)[field] ? field : a;
    }, null);
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);
  useEffect(() => {
    if (createSiteIsSuccess) {
      const successMsg = createSiteData;
      getAllSitesFunc(0, paginationModel.pageSize, filterVal);
      showSuccess(successMsg?.details,'site-create-success');
      onCloseSiteDialog();
    }
  }, [createSiteIsSuccess]);
  useEffect(() => {
    if (updateSiteIsSuccess) {
      (async () => {
        const res = await getSiteDetails({
          study_id: params?.study_id,
          site_id: siteData?.site_id,
        });
        if (res?.data) {
          dispatch(setSiteDetails(res?.data));
        }
      })();
      const successMsg = updateSiteData;
      showSuccess(successMsg?.details,'site-update-success');
      getAllSitesFunc(0, paginationModel.pageSize, filterVal);
      onCloseSiteDialog();
    }
  }, [updateSiteIsSuccess]);
  useEffect(() => {
    if (createSiteIsError || updateSiteIsError) {
      const errorMsg: any = createSiteError ?? updateSiteError;
      handleError(errorMsg);
    }
  }, [createSiteIsError, updateSiteIsError]);
  return (
    <AlertDialog
      open={openAddSiteModal}
      title="Site Details"
      handleClose={onCloseSiteDialog}
      handleConfirm={handleSubmit(submit, onInvalid)}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit(submit, onInvalid)}>
        <DynamicForm
          fieldsJson={FormFields?.siteFields}
          errors={errors}
          register={register}
          control={control}
        />
      </form>
    </AlertDialog>
  );
};

export default SiteDialog;
